import apiClient from './client';

// ── Scarico (§6bis) — prelievo da 1 area R13 ──────────────────────────────────

export interface ScaricoRequest {
  area_origine_id: string;
  /** Forma mono-riga storica: `materiale` + `peso_prelevato_kg` insieme. */
  materiale?: string;
  peso_prelevato_kg?: number;
  /** Forma multi-riga: una riga per materiale, scelta dall'operatore. */
  righe?: { materiale: string; peso_kg: number }[];
  /** Peso che l'operatore aveva chiesto, se l'area non lo copre. */
  peso_richiesto_kg?: number;
  conferma_riduzione?: boolean;
}

export interface Scarico {
  id: string;
  area_origine_id: string;
  materiale: string;
  peso_prelevato_kg: number;
  created_at: string;
  created_by: string | null;
  saldo_risultante_kg: number;
}

/**
 * Esito di UN'OPERAZIONE fisica di prelievo: la testata e le sue N righe.
 * `peso_richiesto_kg` valorizzato = l'area non copriva il peso chiesto e si e'
 * registrato il disponibile (marcatore d'anomalia); `confermato_at` = lo scarto
 * superava la soglia e qualcuno ha confermato.
 */
export interface ScaricoGruppo {
  gruppo_id: string;
  peso_richiesto_kg: number | null;
  confermato_at: string | null;
  righe: Scarico[];
}

export async function createScarico(body: ScaricoRequest): Promise<ScaricoGruppo> {
  const { data } = await apiClient.post<ScaricoGruppo>('/api/scarichi', body);
  return data;
}

/** Correzione di uno scarico: peso, materiale o entrambi (append-only lato BE). */
export async function updateScarico(
  id: string,
  body: { peso_prelevato_kg?: number; materiale?: string },
): Promise<Scarico> {
  const { data } = await apiClient.patch<Scarico>(`/api/scarichi/${id}`, body);
  return data;
}

// ── Produzione (§6ter) — output mono-materiale + N big bag ────────────────────

export interface BigBagIn {
  peso_kg: number;
  data_produzione?: string | null;
  /** V74: obbligatoria sui sacchi NUOVI — il backend risponde 422 senza. */
  tipologia_id: string;
}

export interface ProduzioneRequest {
  // Il CER arriva dall'uscita (#131): uscita_id obbligatorio, il BE deriva il CER.
  // peso_kg NON è un input: = somma dei big bag (calcolato dal BE).
  uscita_id: string;
  area_destinazione_id: string;
  big_bags: BigBagIn[];
  override_capacita?: boolean;
}

export interface BigBag {
  id: string;
  peso_kg: number;
  data_produzione: string | null;
  qr_token: string;
  created_at: string;
  /** Uscita con cui il sacco ha lasciato l'impianto (V71); null = in area.
   *  Peso e data di un sacco uscito sono stampati sull'etichetta e trascritti
   *  sul FIR: il backend risponde 409, qui si evita il tentativo. */
  movimento_uscita_id?: string | null;
  uscita_codice?: string | null;
  /** Definizione commerciale del sacco (V74); null sui sacchi anteriori. */
  tipologia_id?: string | null;
  tipologia_nome?: string | null;
}

export interface Produzione {
  id: string;
  uscita_id: string | null;
  materiale: string;          // snapshot CER (= uscita.cer)
  peso_kg: number;            // = somma big bag
  area_destinazione_id: string;
  created_at: string;
  created_by: string | null;
  big_bags: BigBag[];
  saldo_risultante_kg: number;
  area_capacita_warning: boolean;
  supero_capacita_overridden: boolean;
}

export async function createProduzione(body: ProduzioneRequest): Promise<Produzione> {
  const { data } = await apiClient.post<Produzione>('/api/produzioni', body);
  return data;
}

// Aree RP compatibili con un CER (#132): solo quelle che ammettono il CER + capienza residua > 0.
export interface AreaCompatibile {
  id: string;
  codice: string;
  descrizione: string;
  capacita_kg: number | null;
  saldo_kg: number;
  capienza_residua_kg: number | null;
}

export async function getAreeCompatibili(cerRifiutoId: string): Promise<AreaCompatibile[]> {
  const { data } = await apiClient.get<AreaCompatibile[]>('/api/produzioni/aree-compatibili', {
    params: { cer_rifiuto_id: cerRifiutoId },
  });
  return data;
}

export interface ModificaBigBagResponse {
  id: string;
  produzione_id: string;
  peso_kg: number;
  data_produzione: string | null;
  qr_token: string;
  tipologia_id?: string | null;
  tipologia_nome?: string | null;
  peso_testata_kg: number;       // nuovo peso produzione = somma big bag
  saldo_risultante_kg: number;
}

export async function updateBigBag(
  id: string,
  body: { peso_kg?: number; data_produzione?: string | null },
): Promise<ModificaBigBagResponse> {
  const { data } = await apiClient.patch<ModificaBigBagResponse>(`/api/big-bags/${id}`, body);
  return data;
}

export async function addBigBag(
  produzioneId: string,
  body: { peso_kg: number; data_produzione?: string | null; tipologia_id: string },
): Promise<ModificaBigBagResponse> {
  const { data } = await apiClient.post<ModificaBigBagResponse>(`/api/produzioni/${produzioneId}/big-bags`, body);
  return data;
}

/**
 * Assegna o cambia la tipologia di un big bag ESISTENTE.
 *
 * Endpoint SUO e non un campo di `updateBigBag`: non tocca il ledger (la
 * tipologia sta sotto il codice EER) e la sua guardia e' per SACCO — quella del
 * peso blocca la produzione intera. 409 se il sacco e' gia' uscito.
 */
export async function assegnaTipologiaBigBag(
  id: string, tipologiaId: string,
): Promise<ModificaBigBagResponse> {
  const { data } = await apiClient.patch<ModificaBigBagResponse>(
    `/api/big-bags/${id}/tipologia`, { tipologia_id: tipologiaId });
  return data;
}

export interface BigBagDeleteResponse {
  produzione_id: string;
  peso_testata_kg: number;
  saldo_risultante_kg: number;
}

export async function deleteBigBag(id: string): Promise<BigBagDeleteResponse> {
  const { data } = await apiClient.delete<BigBagDeleteResponse>(`/api/big-bags/${id}`);
  return data;
}

// ── R6-A: eliminazione scarico/produzione con compensazione ledger ────────────

export interface ScaricoDeleteResponse {
  id: string;
  saldo_risultante_kg: number;
  area_capacita_warning: boolean;
  /** 0 = era gia' eliminato (doppio click): nessuno storno a ledger. */
  movimenti_compensazione: number;
}

export interface ProduzioneDeleteResponse {
  id: string;
  saldo_risultante_kg: number;
  /** 0 = era gia' eliminata (doppio click): nessuno storno a ledger. */
  movimenti_compensazione: number;
}

export async function deleteScarico(id: string): Promise<ScaricoDeleteResponse> {
  const { data } = await apiClient.delete<ScaricoDeleteResponse>(`/api/scarichi/${id}`);
  return data;
}

export async function deleteProduzione(id: string): Promise<ProduzioneDeleteResponse> {
  const { data } = await apiClient.delete<ProduzioneDeleteResponse>(`/api/produzioni/${id}`);
  return data;
}

// ── Liste/dettaglio desktop (FASE C) ──────────────────────────────────────────

export interface EcomaticoFields {
  registrato_ecomatico: boolean;
  registrato_ecomatico_at: string | null;
  registrato_ecomatico_da_nome: string | null;
}

export interface ScaricoListItem extends EcomaticoFields {
  /** L'operazione ha registrato meno di quanto chiesto (#116). */
  gruppo_ridotto?: boolean;
  /** La riduzione superava la soglia ed e' stata confermata esplicitamente. */
  gruppo_confermato?: boolean;
  /** Operazione di prelievo di appartenenza, e quante righe VIVE la compongono
   *  (le soft-deleted non contano). Servono alla modale di correzione per dire
   *  che si sta toccando UNA riga di un'operazione fisica unica. `gruppo_id` e'
   *  null sui record storici (pre-V70): riga isolata, mai una testata inventata,
   *  e li' `gruppo_righe` vale 0. */
  gruppo_id: string | null;
  gruppo_righe: number;
  id: string;
  area_origine_id: string;
  area_codice: string | null;
  materiale: string;
  peso_prelevato_kg: number;
  created_at: string;
  created_by: string | null;
  operatore_nome: string | null;
}

export interface ProduzioneListItem extends EcomaticoFields {
  id: string;
  uscita_id: string | null;
  area_destinazione_id: string;
  area_codice: string | null;
  materiale: string;
  peso_kg: number;
  created_at: string;
  created_by: string | null;
  operatore_nome: string | null;
  num_big_bags: number;
  /** Ripartizione (tipologia, kg) dei sacchi (V74): una produzione puo'
   *  impacchettarne piu' d'una. `nome` null = sacchi senza tipologia. */
  tipologie?: { nome: string | null; kg: number }[];
}

export interface ProduzioneDetail {
  id: string;
  uscita_id: string | null;
  area_destinazione_id: string;
  area_codice: string | null;
  materiale: string;
  peso_kg: number;
  created_at: string;
  created_by: string | null;
  operatore_nome: string | null;
  big_bags: BigBag[];
}

export interface ListaFiltri {
  area_id?: string;
  materiale?: string;
  data_da?: string;
  data_a?: string;
  operatore_id?: string;
  registrato_ecomatico?: boolean;   // undefined = tutti
}

export interface RegistrazioneEcomaticoResponse { aggiornati: number; }

export async function registrazioneEcomaticoScarichi(ids: string[], registrato: boolean): Promise<RegistrazioneEcomaticoResponse> {
  const { data } = await apiClient.post<RegistrazioneEcomaticoResponse>('/api/scarichi/registrazione-ecomatico', { ids, registrato });
  return data;
}

export async function registrazioneEcomaticoProduzioni(ids: string[], registrato: boolean): Promise<RegistrazioneEcomaticoResponse> {
  const { data } = await apiClient.post<RegistrazioneEcomaticoResponse>('/api/produzioni/registrazione-ecomatico', { ids, registrato });
  return data;
}

export async function listScarichi(filtri: ListaFiltri = {}): Promise<ScaricoListItem[]> {
  const { data } = await apiClient.get<ScaricoListItem[]>('/api/scarichi', { params: filtri });
  return data;
}

export async function listProduzioni(filtri: ListaFiltri = {}): Promise<ProduzioneListItem[]> {
  const { data } = await apiClient.get<ProduzioneListItem[]>('/api/produzioni', { params: filtri });
  return data;
}

export async function getProduzione(id: string): Promise<ProduzioneDetail> {
  const { data } = await apiClient.get<ProduzioneDetail>(`/api/produzioni/${id}`);
  return data;
}

// ── Etichette PDF A4 (#133) ─────────────────────────────────────────────────────
// Il PDF è generato server-side (WeasyPrint). Scarico come Blob via apiClient così
// l'header Authorization (Bearer) viaggia con la richiesta (un <a>/window.open no).
export async function getEtichettaBigBag(bigBagId: string): Promise<Blob> {
  const { data } = await apiClient.get(`/api/big-bags/${bigBagId}/etichetta`, { responseType: 'blob' });
  return data as Blob;
}

export async function getEtichetteProduzione(produzioneId: string): Promise<Blob> {
  const { data } = await apiClient.get(`/api/produzioni/${produzioneId}/etichette`, { responseType: 'blob' });
  return data as Blob;
}
